import React, { useRef, useState } from 'react'
import debounce from 'lodash.debounce'

import { Owner } from 'services/apiTypes/comment.types'

import UserService from 'services/user.service'
import ToastService from 'services/toast.service'

import AsyncDropdown, { Option } from 'components/elements/AsyncDropdown'
import AvatarCircle from 'components/user/AvatarCircle'

import styles from './asyncUserDropdown.module.scss'

type Props = {
  value?: Owner | string | null
  onChange?: (value: string) => void
  onSelected: (owner: Owner) => void
  triggerClassName?: string
  menuClassName?: string
  disabled?: boolean
  placeholder?: string
  label?: string
}

const UserAutocomplete = ({
  value,
  onSelected,
  onChange,
  triggerClassName = '',
  menuClassName = '',
  disabled,
  placeholder,
  label,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState(new Array<Option>())
  const [results, setResults] = useState(new Array<Owner>())

  const fetchCards = (filter = '') => {
    setLoading(true)

    UserService.basicList(filter)
      .then(res => {
        setResults(res.results)
        setOptions(
          res.results.map(user => ({
            label: <DropdownItem user={user} />,
            value: user.id,
          })),
        )
      })
      .catch(err => {
        console.error(err)

        ToastService.create('Unable to fetch card editions', 'error')
      })
      .finally(() => setLoading(false))
  }

  const debouncedSeach = useRef(debounce(fetchCards, 500)).current

  const handleResultSelect = (value: any) => {
    const selectedUser = results.find(c => c.id === value)

    // Shouldn't be possible
    if (!selectedUser) return

    onSelected(selectedUser)
  }

  const handleSearchChange = (value: string) => {
    setLoading(true)

    debouncedSeach(value)

    if (!onChange) return

    onChange(value)
  }

  return (
    <AsyncDropdown
      label={label}
      loading={loading}
      options={options}
      disabled={disabled}
      menuClassName={menuClassName}
      triggerClassName={`${styles.trigger} ${triggerClassName}`}
      onFocus={() => results.length === 0 && handleSearchChange('')}
      onChange={handleResultSelect}
      onSearchChange={handleSearchChange}
      value={(typeof value === 'string' ? value : value?.username) || undefined}
      placeholder={placeholder ?? ((typeof value === 'string' ? value : value?.username) || 'No user selected')}
    />
  )
}

export default UserAutocomplete

const DropdownItem = ({ user }: { user: Owner }) => (
  <div className={styles.option}>
    <AvatarCircle className={styles.avatar} src={user.avatar} username={user.username} />
    <div className={styles.username}>{user.username} </div>
  </div>
)
